import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Phone, Mail, MapPin } from "lucide-react";
import LogoText from "./LogoText";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import GTranslateWidget from "./GTranslateWidget";

const Footer = () => {
  const { getConfig } = useSiteConfig();
  
  return (
    <footer className="bg-gradient-to-br from-primary via-primary to-primary/95 text-primary-foreground">
      {/* Decorative Top Border */}
      <div className="h-1 bg-gradient-to-r from-secondary via-tropical to-secondary" />
      
      <div className="container mx-auto px-4">
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="space-y-6">
            <LogoText useFooterLogo={true} className="h-16" />
            <p className="text-primary-foreground/80 text-sm leading-relaxed">
              {getConfig('footer_description') || 'Your premier transfer and tour service in Punta Cana. Experience the Caribbean with comfort, reliability, and style.'}
            </p>
            <div className="flex space-x-4">
              <GTranslateWidget />
            </div>
            <div className="flex space-x-3 mt-4">
              {getConfig('social_facebook')?.enabled && (
                <a href={getConfig('social_facebook')?.url || '#'} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" target="_blank" rel="noopener noreferrer">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {getConfig('social_instagram')?.enabled && (
                <a href={getConfig('social_instagram')?.url || '#'} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" target="_blank" rel="noopener noreferrer">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {getConfig('social_tiktok')?.enabled && (
                <a href={getConfig('social_tiktok')?.url || '#'} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" target="_blank" rel="noopener noreferrer">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Us" },
                { to: "/transfers", label: "Transfers" },
                { to: "/tours", label: "Tours" },
                { to: "/blog", label: "Blog" },
                { to: "/contact", label: "Contact" },
                { to: "/faq", label: "FAQ" }
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm link-underline">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Our Services</h3>
            <ul className="space-y-3">
              {[
                { to: "/transfers", label: "Airport Transfers" },
                { to: "/transfers", label: "Hotel Transfers" },
                { to: "/tours", label: "Private Tours" },
                { to: "/tours", label: "Group Tours" }
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-primary-foreground/80 hover:text-secondary transition-colors text-sm link-underline">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 text-sm">
                <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-primary-foreground/90 font-medium">{getConfig('contact_phone') || '+1 (809) 123-4567'}</p>
                  <p className="text-primary-foreground/60 text-xs">24/7 Available</p>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-primary-foreground/90 font-medium">{getConfig('contact_email') || 'info@strpuntacana.com'}</p>
                  <p className="text-primary-foreground/60 text-xs">Email Support</p>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-primary-foreground/90 font-medium">Punta Cana</p>
                  <p className="text-primary-foreground/60 text-xs">Dominican Republic</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-primary-foreground/60 text-sm">© {new Date().getFullYear()} Servicio Turistico Rachel. All rights reserved.</p>
            <div className="flex space-x-6 text-sm">
              <Link to="/terms" className="text-primary-foreground/60 hover:text-secondary transition-colors">Terms & Conditions</Link>
              <Link to="/privacy" className="text-primary-foreground/60 hover:text-secondary transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
